// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";
import { env } from "app/_shared/env.mjs";

const CHUNK_RELOAD_KEY = "chunk_reload_attempted";

const NETWORK_DIAGNOSTIC_BREADCRUMB_LIMIT = 10;

/**
 * Detects chunk loading errors that occur after deployments when cached HTML
 * references old JavaScript chunks that no longer exist. The server returns
 * HTML (404 page) instead of JS, causing "SyntaxError: Unexpected token '<'".
 */
const isChunkLoadError = (message: string, source?: string): boolean => {
  const isUnexpectedToken = message.includes("Unexpected token");
  const isChunkFile = source?.includes("/_next/static/chunks/") ?? false;
  return isUnexpectedToken && isChunkFile;
};

/**
 * Handles chunk loading errors by reloading the page once to fetch fresh chunks.
 * Uses sessionStorage to prevent infinite reload loops.
 */
const handleChunkLoadError = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    const hasAttemptedReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (hasAttemptedReload) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return false;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
    window.location.reload();
    return true;
  } catch {
    // Storage unavailable (private browsing, quota exceeded, etc.)
    // Skip reload to avoid potential infinite loops without tracking
    return false;
  }
};

// Set up global error handler for chunk loading errors
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    const message = event.message || "";
    const source = event.filename || "";

    if (isChunkLoadError(message, source)) {
      handleChunkLoadError();
    }
  });
}

try {
  Sentry.init({
    dsn: await fetchSentryDsn(),

    // Add optional integrations for additional features
    integrations: [
      // Log replays
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/replay/
      Sentry.replayIntegration(),
      // Log console errors
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/captureconsole/
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      // Extract all non-native attributes from error and attach as extra data
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/extraerrordata/
      Sentry.extraErrorDataIntegration({ depth: 10 }),
      // Capture errors on failed requests from Fetch and XHR, and get request/response information
      // https://docs.sentry.io/platforms/javascript/configuration/integrations/httpclient/
      Sentry.httpClientIntegration(),
    ],

    release: env.NEXT_PUBLIC_VERSION_HASH,

    // Which errors to ignore from backend
    ignoreErrors: [
      "Msg: Invalid session", // Expected when next-auth session is invalid
      "%c << query", // Expect we threw a proper backend error, no need to look at console log
      "Load failed", // Seems to be related primarily to network issues, or when we deploy new versions
      /Unexpected token.*</, // Chunk loading errors after deployments - auto-recovered via page reload
      "Do not use scan directly in a Server Component module. It should only be used in a Client Component", // Known issue, fixing would require loading scan from CDN prior to React to avoid a __H trap. Only for staff with this enabled, safe to ignore.
      "Failed to fetch RSC payload", // Related to new deployments, safe to ignore, can't do much here
      "Failed to fetch (ai.leopharma-app.com)", // Typically related to new deployments, safe to ignore. Can result in false negatives!
      "Hydration Error", // Usually not visually affecting users, and we get too many from various browser extensions injecting things
      "The response was filtered due", // Error gracefully handled in chat/route.ts
      "[next-auth][error][CLIENT_FETCH_ERROR]", // Ignore when we cannot fetch session, e.g. in chats
      /Event.*\(type=error\).*captured as promise rejection/, // Link/resource load failures on mobile browsers
    ],

    // Set the environment
    environment: env.NEXT_PUBLIC_ENV,

    tracePropagationTargets: [
      "localhost",
      /^https:\/\/127\.0\.0\.1:14040\/api/,
      /^\//,
      /^https:\/\/dev\.ai\.leopharma-app\.com\/api/,
      /^https:\/\/test\.ai\.leopharma-app\.com\/api/,
      /^https:\/\/ai\.leopharma-app\.com\/api/,
    ],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 1,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.0,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    // Filter out specific errors that we can't fix and are transient infrastructure issues
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? "";
      const mechanism = event.exception?.values?.[0]?.mechanism?.type;
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];

      // HUB-PROD-5N: browsers sometimes report only `TypeError: network error`
      // through console capture, without a stack trace or failing request URL.
      // Keep the event, but enrich it with nearby fetch breadcrumbs so future
      // occurrences show which Chat AI/RSC/tRPC/blob calls happened immediately
      // before the low-information browser network error.
      const isConsoleNetworkError =
        mechanism === "console" &&
        event.logger === "console" &&
        message === "network error";

      if (isConsoleNetworkError) {
        const recentFetches = event.breadcrumbs
          ?.filter((breadcrumb) => breadcrumb.category === "fetch")
          .slice(-NETWORK_DIAGNOSTIC_BREADCRUMB_LIMIT)
          .map((breadcrumb) => {
            const data = breadcrumb.data as Record<string, unknown> | undefined;

            return {
              timestamp: breadcrumb.timestamp,
              level: breadcrumb.level,
              method: data?.method,
              url: data?.url,
              status_code: data?.status_code,
            };
          });
        const lastFetch = recentFetches?.at(-1);

        event.contexts = {
          ...event.contexts,
          network_error_diagnostics: {
            type: "network_error_diagnostics",
            current_url:
              typeof window !== "undefined" ? window.location.href : undefined,
            recent_fetches: recentFetches,
          },
        };

        event.tags = {
          ...event.tags,
          diagnostic_type: "console_network_error",
          last_fetch_url:
            typeof lastFetch?.url === "string" ? lastFetch.url : undefined,
          last_fetch_status:
            typeof lastFetch?.status_code === "number" ||
            typeof lastFetch?.status_code === "string"
              ? String(lastFetch.status_code)
              : undefined,
        };
      }

      // Filter 504 Gateway Timeout errors that occur during Next.js RSC fetching
      // These are transient infrastructure issues during client-side navigation
      if (message.includes("HTTP Client Error with status code: 504")) {
        const isRscFetchError = frames.some(
          (frame) =>
            frame.filename?.includes("fetch-server-response") ||
            frame.filename?.includes("router-reducer"),
        );
        if (isRscFetchError) {
          return null;
        }
      }

      // Filter Next.js Edge browser RSC chunk loading bug (HUB-PROD-NJ)
      // The error occurs in Next.js's encodeURIPath when webpack's getChunkScriptFilename returns undefined
      // Next.js handles this gracefully by falling back to browser navigation
      if (
        message.includes(
          "Cannot read properties of undefined (reading 'split')",
        )
      ) {
        const isNextJsEncodeUriPathError = frames.some(
          (frame) =>
            frame.filename?.includes("encode-uri-path") ||
            frame.filename?.includes("app-webpack"),
        );
        if (isNextJsEncodeUriPathError) {
          return null;
        }
      }

      return event;
    },
  });
} catch (error) {
  console.error("Failed to initialize Sentry:", error);
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

/**
 * Fetches the Sentry DSN from the server tRPC endpoint
 * @returns The Sentry DSN
 */
async function fetchSentryDsn(): Promise<string> {
  try {
    const response = await fetch("/api/trpc/utils.sentryDsn", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as {
      result: { data: { json: { sentryDsn: string } } };
    };
    const dsn = data?.result?.data?.json?.sentryDsn ?? "";

    return dsn;
  } catch (error) {
    console.error("Failed to fetch Sentry DSN:", error);
    // Fallback to empty string to prevent initialization errors
    return "";
  }
}
